import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SwapStudentData {
  id: string;
  user_id: string;
  name: string;
  class_name: string;
  section: string | null;
  roll_no: number;
  admission_no: string;
  profile_picture_url: string;
  updated_at: string;
}

interface SwapProfilePictureModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * BULK-SELECT mode  : both studentA and studentB are provided (user picked 2 rows).
   * ACTION-COLUMN mode: only studentA is provided; studentB=null — modal shows a picker
   *                     so the admin can search and choose the swap partner.
   */
  studentA: SwapStudentData | null;
  studentB: SwapStudentData | null;
  /** Required for action-column mode so the picker has a list to search through */
  allStudents?: SwapStudentData[];
}

// ─── Scenario analysis ────────────────────────────────────────────────────────

type SwapScenario =
  | 'both_have_pic'    // normal full swap
  | 'only_a_has_pic'   // A's pic moves to B; A becomes pictureless
  | 'only_b_has_pic'   // B's pic moves to A; B becomes pictureless
  | 'neither_has_pic'; // nothing to swap — block the action

const getScenario = (a: SwapStudentData, b: SwapStudentData): SwapScenario => {
  const hasA = Boolean(a.profile_picture_url?.trim());
  const hasB = Boolean(b.profile_picture_url?.trim());
  if (hasA && hasB)  return 'both_have_pic';
  if (hasA && !hasB) return 'only_a_has_pic';
  if (!hasA && hasB) return 'only_b_has_pic';
  return 'neither_has_pic';
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildImgUrl = (url: string, bust: string): string => {
  if (!url) return '';
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}cb=${bust}`;
};

const getInitials = (name: string): string =>
  name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

// ─── Avatar ───────────────────────────────────────────────────────────────────

const Avatar: React.FC<{
  src: string;
  name: string;
  bust: string;
  size?: number;
  ring?: string;
  badge?: React.ReactNode;
}> = ({ src, name, bust, size = 80, ring, badge }) => {
  const [err, setErr] = useState(false);
  useEffect(() => setErr(false), [src]);

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <div style={{
        width: size, height: size, borderRadius: '50%',
        border: `3px solid ${ring ?? '#e5e7eb'}`,
        overflow: 'hidden',
        background: 'linear-gradient(135deg,#dbeafe,#eff6ff)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: ring ? `0 0 0 3px ${ring}22, 0 4px 14px rgba(0,0,0,.12)` : '0 4px 12px rgba(0,0,0,.1)',
        transition: 'all .35s cubic-bezier(.34,1.56,.64,1)',
      }}>
        {src && !err ? (
          <img
            src={buildImgUrl(src, bust)} alt={name}
            onError={() => setErr(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <span style={{ fontSize: size * 0.28, fontWeight: 800, color: '#3b82f6', userSelect: 'none' }}>
            {getInitials(name)}
          </span>
        )}
      </div>
      {badge && (
        <div style={{ position: 'absolute', bottom: -4, right: -4 }}>{badge}</div>
      )}
    </div>
  );
};

// ─── No-photo placeholder ─────────────────────────────────────────────────────

const NoPicPlaceholder: React.FC<{ size?: number }> = ({ size = 80 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%',
    border: '3px dashed #d1d5db', background: '#f9fafb',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', gap: 2, flexShrink: 0,
    boxShadow: '0 2px 8px rgba(0,0,0,.05)',
  }}>
    <i className="pi pi-image" style={{ fontSize: size * 0.28, color: '#d1d5db' }} />
    {size >= 60 && (
      <span style={{ fontSize: 8, color: '#9ca3af', fontWeight: 700, letterSpacing: '.5px' }}>NO PHOTO</span>
    )}
  </div>
);

// ─── Scenario banner ──────────────────────────────────────────────────────────

const ScenarioBanner: React.FC<{
  scenario: SwapScenario;
  nameA: string;
  nameB: string;
}> = ({ scenario, nameA, nameB }) => {
  if (scenario === 'neither_has_pic') {
    return (
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '12px 14px', borderRadius: 12,
        background: '#fef2f2', border: '1.5px solid #fecaca',
      }}>
        <i className="pi pi-times-circle" style={{ fontSize: 15, color: '#ef4444', marginTop: 1, flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#dc2626', marginBottom: 3 }}>
            Cannot swap — no photos exist
          </div>
          <div style={{ fontSize: 12, color: '#b91c1c', lineHeight: 1.65 }}>
            Neither <strong>{nameA}</strong> nor <strong>{nameB}</strong> has a profile picture.
            Upload at least one photo first, then return here to swap.
          </div>
        </div>
      </div>
    );
  }

  if (scenario === 'only_a_has_pic' || scenario === 'only_b_has_pic') {
    const hasPicName = scenario === 'only_a_has_pic' ? nameA : nameB;
    const noPicName  = scenario === 'only_a_has_pic' ? nameB : nameA;
    return (
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '12px 14px', borderRadius: 12,
        background: '#fffbeb', border: '1.5px solid #fde68a',
      }}>
        <i className="pi pi-exclamation-triangle" style={{ fontSize: 15, color: '#d97706', marginTop: 1, flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 3 }}>
            One-sided transfer
          </div>
          <div style={{ fontSize: 12, color: '#78350f', lineHeight: 1.65 }}>
            Only <strong>{hasPicName}</strong> has a photo right now.
            After confirming, <strong>{noPicName}</strong> will receive the photo and{' '}
            <strong>{hasPicName}</strong> will be left with <em>no profile picture</em>.
            You can swap back at any time to undo this.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 14px', borderRadius: 12,
      background: 'rgba(59,130,246,.06)', border: '1px solid rgba(59,130,246,.15)',
    }}>
      <i className="pi pi-info-circle" style={{ fontSize: 14, color: '#3b82f6', flexShrink: 0 }} />
      <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
        Only the <strong>profile picture URLs</strong> are swapped — no files move on the server.
        Instant and reversible by swapping again.
      </div>
    </div>
  );
};

// ─── Student card ─────────────────────────────────────────────────────────────

const StudentCard: React.FC<{
  student: SwapStudentData;
  label: string;
  accentColor: string;
  /** URL to render instead of student's own pic (preview mode) */
  previewSrc?: string;
  /** Show NoPicPlaceholder instead of avatar (preview: student will lose their pic) */
  previewNoPic?: boolean;
  bust: string;
  animClass?: string;
}> = ({ student, label, accentColor, previewSrc, previewNoPic, bust, animClass }) => {
  const hasPic    = Boolean(student.profile_picture_url?.trim());
  const displaySrc = previewSrc !== undefined ? previewSrc : student.profile_picture_url;

  return (
    <div className={animClass} style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
      padding: '22px 14px 18px', borderRadius: 18,
      border: `2px solid ${accentColor}`,
      background: `${accentColor}0d`,
      minWidth: 148, flex: 1, position: 'relative',
      transition: 'background .4s ease, border-color .4s ease',
    }}>
      {/* Label pill */}
      <div style={{
        position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
        padding: '3px 14px', borderRadius: 20,
        background: accentColor, color: 'white',
        fontSize: 10, fontWeight: 800, letterSpacing: '.8px', whiteSpace: 'nowrap',
      }}>
        {label}
      </div>

      {/* Photo */}
      {previewNoPic ? (
        <NoPicPlaceholder size={80} />
      ) : displaySrc ? (
        <Avatar
          src={displaySrc} name={student.name} bust={bust} size={80} ring={accentColor}
          badge={
            !hasPic && previewSrc ? (
              <div style={{
                width: 20, height: 20, borderRadius: '50%',
                background: '#10b981', border: '2px solid white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <i className="pi pi-check" style={{ fontSize: 9, color: 'white' }} />
              </div>
            ) : undefined
          }
        />
      ) : (
        <NoPicPlaceholder size={80} />
      )}

      {/* Info */}
      <div style={{ textAlign: 'center', minWidth: 0, width: '100%' }}>
        <div style={{
          fontSize: 13, fontWeight: 700, color: '#1f2937',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {student.name}
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>
          {student.class_name}{student.section ? ` · ${student.section}` : ''}
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>Roll #{student.roll_no}</div>

        {/* Current photo status */}
        <div style={{
          marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '3px 9px', borderRadius: 20,
          background: hasPic ? '#f0fdf4' : '#f9fafb',
          border: `1px solid ${hasPic ? '#86efac' : '#e5e7eb'}`,
          fontSize: 10, fontWeight: 700,
          color: hasPic ? '#059669' : '#9ca3af',
        }}>
          <i className={hasPic ? 'pi pi-image' : 'pi pi-ban'} style={{ fontSize: 9 }} />
          {hasPic ? 'Has photo' : 'No photo'}
        </div>
      </div>
    </div>
  );
};

// ─── Student picker (action-column flow — pick student B from a list) ─────────

const StudentPicker: React.FC<{
  students: SwapStudentData[];
  excludeId: string;
  onPick: (s: SwapStudentData) => void;
  bust: string;
}> = ({ students, excludeId, onPick, bust }) => {
  const [q, setQ] = useState('');

  const filtered = students.filter(
    (s) =>
      s.id !== excludeId &&
      (s.name.toLowerCase().includes(q.toLowerCase()) ||
        s.admission_no.toLowerCase().includes(q.toLowerCase()) ||
        String(s.roll_no).includes(q))
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
        Choose the second student to swap photos with:
      </div>

      {/* Search box */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'white', borderRadius: 10, padding: '8px 12px',
        border: '1.5px solid #e2e8f0',
        boxShadow: '0 1px 4px rgba(0,0,0,.05)',
      }}>
        <i className="pi pi-search" style={{ fontSize: 13, color: '#3b82f6' }} />
        <input
          autoFocus
          type="text"
          placeholder="Search by name, roll no or admission no…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, color: '#1f2937', background: 'transparent' }}
        />
        {q && (
          <button onClick={() => setQ('')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex' }}>
            <i className="pi pi-times" style={{ fontSize: 11 }} />
          </button>
        )}
      </div>

      {/* List */}
      <div style={{ maxHeight: 290, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6,
        paddingRight: 2 }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '28px 0', color: '#9ca3af', fontSize: 13 }}>
            <i className="pi pi-search" style={{ fontSize: 24, marginBottom: 8, display: 'block' }} />
            No students found
          </div>
        ) : (
          filtered.map((s) => {
            const hasPic = Boolean(s.profile_picture_url?.trim());
            return (
              <button
                key={s.id}
                onClick={() => onPick(s)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 12px', borderRadius: 12,
                  background: 'white', border: '1.5px solid #e5e7eb',
                  cursor: 'pointer', textAlign: 'left', transition: 'all .15s',
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLButtonElement;
                  el.style.borderColor = '#93c5fd';
                  el.style.background  = '#eff6ff';
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLButtonElement;
                  el.style.borderColor = '#e5e7eb';
                  el.style.background  = 'white';
                }}
              >
                {hasPic
                  ? <Avatar src={s.profile_picture_url} name={s.name} bust={bust} size={40} />
                  : <NoPicPlaceholder size={40} />
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    {s.class_name}{s.section ? ` · ${s.section}` : ''} · Roll #{s.roll_no}
                  </div>
                </div>
                <div style={{
                  flexShrink: 0, fontSize: 10, fontWeight: 700,
                  padding: '2px 9px', borderRadius: 12,
                  background: hasPic ? '#f0fdf4' : '#f9fafb',
                  border: `1px solid ${hasPic ? '#86efac' : '#e5e7eb'}`,
                  color: hasPic ? '#059669' : '#9ca3af',
                }}>
                  {hasPic ? '📷 Has photo' : 'No photo'}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const SwapProfilePictureModal: React.FC<SwapProfilePictureModalProps> = ({
  open,
  onClose,
  onSuccess,
  studentA,
  studentB: studentBProp,
  allStudents = [],
}) => {
  const toast = useRef<Toast>(null);
  const [submitting, setSubmitting]   = useState(false);
  const [done, setDone]               = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isClosing, setIsClosing]     = useState(false);
  const [studentB, setStudentB]       = useState<SwapStudentData | null>(studentBProp);
  const bust                          = useRef(Date.now().toString());

  // Keep studentB in sync when prop changes (bulk-select mode re-opens modal)
  useEffect(() => { setStudentB(studentBProp); }, [studentBProp]);

  useEffect(() => {
    if (open) {
      setDone(false);
      setShowPreview(false);
      setSubmitting(false);
      setStudentB(studentBProp);
      bust.current = Date.now().toString();
    }
  }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.swap-pic-modal .p-dialog') as HTMLElement | null;
      const mask   = document.querySelector('.swap-pic-modal')           as HTMLElement | null;
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask)   mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => { onClose(); setIsClosing(false); }, 380);
  };

  // ── Scenario ───────────────────────────────────────────────────────────────
  const scenario: SwapScenario | null =
    studentA && studentB ? getScenario(studentA, studentB) : null;

  const canSwap = scenario !== null && scenario !== 'neither_has_pic';

  // ── Preview logic ──────────────────────────────────────────────────────────
  // What photo does A show in preview?
  const previewSrcA   = showPreview && studentB ? (studentB.profile_picture_url || '') : undefined;
  const previewNoPicA = showPreview && scenario === 'only_a_has_pic'; // A gives its pic away

  // What photo does B show in preview?
  const previewSrcB   = showPreview && studentA ? (studentA.profile_picture_url || '') : undefined;
  const previewNoPicB = showPreview && scenario === 'only_b_has_pic'; // B gives its pic away

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSwap = async () => {
    if (!studentA || !studentB || !canSwap) return;
    setSubmitting(true);
    try {
      const response = await apiClient.post('/students/swap-profile-pictures', {
        student_id_a: studentA.id,
        student_id_b: studentB.id,
      });
      const data = response.data;

      if (data.status === 'success') {
        setDone(true);
        toast.current?.show({
          severity: 'success',
          summary:  'Swapped!',
          detail:   `Profile pictures swapped between ${studentA.name} and ${studentB.name}`,
          life:     4000,
        });
        onSuccess();
        setTimeout(() => handleClose(), 1600);
      } else {
        toast.current?.show({
          severity: 'error',
          summary:  'Swap Failed',
          detail:   data.message || 'An unexpected error occurred',
          life:     5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary:  'Network Error',
        detail:   err.response?.data?.message || 'An error occurred',
        life:     5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!studentA) return null;

  // ── Header ─────────────────────────────────────────────────────────────────
  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '4px 0' }}>
      <div style={{
        width: 48, height: 48, borderRadius: 14,
        background: done
          ? 'linear-gradient(135deg,#059669,#10b981)'
          : 'linear-gradient(135deg,#1e3a8a,#3b82f6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, border: '3px solid white',
        boxShadow: done ? '0 6px 18px rgba(16,185,129,.35)' : '0 6px 18px rgba(59,130,246,.3)',
        transition: 'all .4s ease',
      }}>
        <i className={done ? 'pi pi-check' : 'pi pi-sync'}
          style={{ fontSize: 20, color: 'white' }} />
      </div>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: done ? '#059669' : '#1e40af', transition: 'color .3s' }}>
          {done
            ? 'Swap Complete!'
            : studentB
              ? 'Swap Profile Pictures'
              : 'Choose Swap Partner'}
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
          {done
            ? 'Profile pictures have been exchanged successfully'
            : studentB
              ? 'Review the swap details and confirm'
              : `Picking a swap partner for ${studentA.name}`}
        </div>
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{
          width: '94vw',
          maxWidth: studentB ? 590 : 530,
          borderRadius: 22,
          overflow: 'hidden',
          transition: 'max-width .3s ease',
        }}
        contentStyle={{
          padding: '24px',
          background: 'linear-gradient(180deg,#f8fafc,#f1f5f9)',
        }}
        headerStyle={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '18px 24px',
          borderRadius: '22px 22px 0 0',
        }}
        modal
        draggable={false}
        resizable={false}
        blockScroll
        dismissableMask={!submitting}
        maskClassName="swap-pic-modal"
      >
        <style>{`
          .swap-pic-modal { backdrop-filter:blur(5px); background-color:rgba(0,0,0,.25)!important; animation:swpFadeIn .3s ease; }
          .swap-pic-modal.mask-closing { animation:swpFadeOut .38s ease!important; }
          @keyframes swpFadeIn  { from{opacity:0}to{opacity:1} }
          @keyframes swpFadeOut { from{opacity:1}to{opacity:0} }
          .swap-pic-modal .p-dialog { animation:swpSlideIn .32s cubic-bezier(.34,1.56,.64,1); box-shadow:0 24px 64px -12px rgba(0,0,0,.3)!important; }
          .swap-pic-modal .p-dialog.dialog-closing { animation:swpSlideOut .38s ease!important; }
          @keyframes swpSlideIn  { from{opacity:0;transform:scale(.9) translateY(-18px)}to{opacity:1;transform:scale(1) translateY(0)} }
          @keyframes swpSlideOut { from{opacity:1;transform:scale(1)}to{opacity:0;transform:scale(.88) translateY(20px)} }
          @keyframes swpSpin     { from{transform:rotate(0)}to{transform:rotate(360deg)} }
          @keyframes swpBounce   { 0%,100%{transform:scale(1)}50%{transform:scale(1.22)} }
          @keyframes swpFlyL     { from{opacity:0;transform:translateX(14px)}to{opacity:1;transform:translateX(0)} }
          @keyframes swpFlyR     { from{opacity:0;transform:translateX(-14px)}to{opacity:1;transform:translateX(0)} }
          .anim-fly-l { animation:swpFlyL .3s cubic-bezier(.34,1.56,.64,1); }
          .anim-fly-r { animation:swpFlyR .3s cubic-bezier(.34,1.56,.64,1); }
          .swap-spin   { animation:swpSpin .9s linear infinite; }
          .swap-done-bounce { animation:swpBounce .5s cubic-bezier(.34,1.56,.64,1); }
          .preview-toggle-btn {
            display:inline-flex; align-items:center; gap:6px;
            padding:6px 18px; border-radius:20px;
            font-size:12px; font-weight:700; cursor:pointer;
            border:2px solid #bfdbfe; background:white; color:#3b82f6;
            transition:all .18s; letter-spacing:.3px;
          }
          .preview-toggle-btn:hover { background:#eff6ff; border-color:#93c5fd; }
          .preview-toggle-btn.active {
            background:linear-gradient(135deg,#dbeafe,#eff6ff);
            border-color:#3b82f6; color:#1d4ed8;
            box-shadow:0 2px 10px rgba(59,130,246,.22);
          }
          .change-partner-btn {
            display:inline-flex; align-items:center; gap:6px;
            padding:6px 14px; border-radius:20px;
            border:1.5px solid #e5e7eb; background:white;
            font-size:12px; font-weight:600; color:#64748b;
            cursor:pointer; transition:all .15s;
          }
          .change-partner-btn:hover { border-color:#93c5fd; color:#3b82f6; background:#f8faff; }
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* ── Success banner ── */}
          {done && (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
              padding: '24px', borderRadius: 18,
              background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)',
              border: '2px solid #86efac',
            }}>
              <div className="swap-done-bounce" style={{ fontSize: 48 }}>✅</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#065f46' }}>
                Photos swapped successfully!
              </div>
              <div style={{ fontSize: 12, color: '#059669' }}>Closing automatically…</div>
            </div>
          )}

          {/* ── Action-column flow: student B not yet chosen ── */}
          {!done && !studentB && (
            <StudentPicker
              students={allStudents}
              excludeId={studentA.id}
              onPick={(s) => { setStudentB(s); setShowPreview(false); }}
              bust={bust.current}
            />
          )}

          {/* ── Both students set: main swap UI ── */}
          {!done && studentB && (
            <>
              {/* Cards */}
              <div style={{ display: 'flex', alignItems: 'stretch', gap: 14 }}>
                <StudentCard
                  student={studentA}
                  label={showPreview ? '← WILL RECEIVE' : 'STUDENT A'}
                  accentColor="#3b82f6"
                  previewSrc={previewSrcA}
                  previewNoPic={previewNoPicA}
                  bust={bust.current}
                  animClass={showPreview ? 'anim-fly-l' : undefined}
                />

                {/* Centre arrow */}
                <div style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0 2px',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: submitting
                      ? 'linear-gradient(135deg,#1e40af,#3b82f6)'
                      : showPreview
                        ? 'linear-gradient(135deg,#f59e0b,#d97706)'
                        : 'linear-gradient(135deg,#e2e8f0,#f1f5f9)',
                    border: '2.5px solid white',
                    boxShadow: '0 3px 10px rgba(0,0,0,.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all .3s ease',
                  }}>
                    <i
                      className={`pi pi-arrows-h${submitting ? ' swap-spin' : ''}`}
                      style={{ fontSize: 18, color: (submitting || showPreview) ? 'white' : '#64748b' }}
                    />
                  </div>
                  {showPreview && (
                    <span style={{ fontSize: 9, fontWeight: 800, color: '#f59e0b', letterSpacing: '.6px', textAlign: 'center' }}>
                      AFTER<br />SWAP
                    </span>
                  )}
                </div>

                <StudentCard
                  student={studentB}
                  label={showPreview ? 'WILL RECEIVE →' : 'STUDENT B'}
                  accentColor={
                    scenario === 'only_a_has_pic' || scenario === 'only_b_has_pic'
                      ? '#f59e0b'
                      : '#10b981'
                  }
                  previewSrc={previewSrcB}
                  previewNoPic={previewNoPicB}
                  bust={bust.current}
                  animClass={showPreview ? 'anim-fly-r' : undefined}
                />
              </div>

              {/* Scenario banner */}
              {scenario && (
                <ScenarioBanner scenario={scenario} nameA={studentA.name} nameB={studentB.name} />
              )}

              {/* Preview toggle + change partner */}
              {scenario !== 'neither_has_pic' && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    className={`preview-toggle-btn${showPreview ? ' active' : ''}`}
                    onClick={() => setShowPreview((v) => !v)}
                  >
                    <i className={showPreview ? 'pi pi-eye-slash' : 'pi pi-eye'} style={{ fontSize: 12 }} />
                    {showPreview ? 'Hide Preview' : 'Preview After Swap'}
                  </button>

                  {/* Only show "Change partner" in action-column flow (no prop studentB) */}
                  {!studentBProp && (
                    <button
                      className="change-partner-btn"
                      onClick={() => { setStudentB(null); setShowPreview(false); }}
                    >
                      <i className="pi pi-refresh" style={{ fontSize: 11 }} />
                      Change partner
                    </button>
                  )}
                </div>
              )}

              {showPreview && scenario !== 'neither_has_pic' && (
                <div style={{
                  padding: '8px 14px', borderRadius: 10, textAlign: 'center',
                  background: '#fffbeb', border: '1px solid #fde68a',
                  fontSize: 12, color: '#92400e', fontWeight: 500,
                }}>
                  👆 Cards above show exactly how photos will look <em>after</em> confirming the swap
                </div>
              )}

              {/* Footer */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 10, paddingTop: 6, borderTop: '1px solid #e5e7eb',
              }}>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                  {scenario === 'neither_has_pic' && '⛔ Swap not available — no photos'}
                  {scenario === 'both_have_pic'   && '↔ Both photos will be exchanged'}
                  {(scenario === 'only_a_has_pic' || scenario === 'only_b_has_pic') && '➡ One photo will transfer to the other student'}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <Button
                    label="Cancel"
                    icon="pi pi-times"
                    className="p-button-outlined"
                    style={{ borderRadius: 10, fontSize: 13 }}
                    onClick={handleClose}
                    disabled={submitting}
                  />
                  <Button
                    label={submitting ? 'Swapping…' : scenario === 'neither_has_pic' ? 'Cannot Swap' : 'Confirm Swap'}
                    icon={submitting ? 'pi pi-spin pi-spinner' : 'pi pi-sync'}
                    disabled={!canSwap || submitting}
                    style={{
                      borderRadius: 10, fontSize: 13, border: 'none', padding: '9px 20px',
                      background: canSwap ? 'linear-gradient(135deg,#1e40af,#3b82f6)' : '#e5e7eb',
                      color: canSwap ? 'white' : '#9ca3af',
                      boxShadow: canSwap ? '0 4px 14px rgba(59,130,246,.35)' : 'none',
                      opacity: (!canSwap || submitting) ? 0.6 : 1,
                      transition: 'all .2s',
                    }}
                    onClick={handleSwap}
                  />
                </div>
              </div>
            </>
          )}

          {/* Footer when in picker mode (no B yet) */}
          {!done && !studentB && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 6, borderTop: '1px solid #e5e7eb' }}>
              <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-outlined"
                style={{ borderRadius: 10, fontSize: 13 }}
                onClick={handleClose}
              />
            </div>
          )}

        </div>
      </Dialog>
    </>
  );
};

export default SwapProfilePictureModal;